import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getSharedList, saveTalent, unsaveTalent, SharedListResponse } from '../services/api';
import AppNavbar from '../components/AppNavbar';
import { DeveloperCard } from '../components/SearchResults';
import QuickViewModal from '../components/QuickViewModal';
import { ArrowLeft, Heart, Share, Lock } from 'lucide-react';
import { SoftwareTalent } from '../types';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const SharedListPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<SharedListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'forbidden' | 'notfound' | 'unknown' | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [savedAll, setSavedAll] = useState(false);
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  const [quickViewTalent, setQuickViewTalent] = useState<SoftwareTalent | null>(null);

  const domainFromUrl = searchParams.get('domain');
  const [activeDomain, setActiveDomainState] = useState<string | null>(null);

  const setActiveDomain = useCallback((domain: string | null) => {
    setActiveDomainState(domain);
    if (domain) {
      const label = data?.domains.find(x => x.domain === domain)?.label ?? domain;
      setSearchParams({ domain: label }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [data, setSearchParams]);

  useEffect(() => {
    if (!shareId) return;
    getSharedList(shareId)
      .then((result) => {
        setData(result);
        if (domainFromUrl && result.domains.length > 1) {
          const match = result.domains.find(
            d => d.domain === domainFromUrl || d.label.toLowerCase() === domainFromUrl.toLowerCase()
          );
          if (match) setActiveDomainState(match.domain);
        }
      })
      .catch((err) => {
        const status = axios.isAxiosError(err) ? err.response?.status : null;
        setError(status === 403 ? 'forbidden' : status === 404 ? 'notfound' : 'unknown');
      })
      .finally(() => setLoading(false));
  }, [shareId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allTalent: SoftwareTalent[] = (data?.domains ?? []).flatMap(
    ({ engineers }) => engineers as SoftwareTalent[]
  );
  const totalCount = allTalent.length;

  const handleSaveAll = async () => {
    if (savingAll || savedAll) return;
    setSavingAll(true);
    try {
      const pairs = (data?.domains ?? []).flatMap(({ domain, engineers }) =>
        (engineers as SoftwareTalent[]).map((e) => ({ id: e.id, domain }))
      );
      await Promise.all(pairs.map(({ id, domain }) => saveTalent(id, domain)));
      const next = new Set<number>();
      pairs.forEach(({ id }) => next.add(id));
      setSavedIds(next);
      setSavedAll(true);
    } finally {
      setSavingAll(false);
    }
  };

  const handleSave = (talentId: number, domain?: string) => {
    saveTalent(talentId, domain ?? 'SWE').then(() => {
      setSavedIds((prev) => { const next = new Set(prev); next.add(talentId); return next; });
    });
  };

  const handleUnsave = (talentId: number, domain?: string) => {
    unsaveTalent(talentId, domain ?? 'SWE').then(() => {
      setSavedIds((prev) => { const next = new Set(prev); next.delete(talentId); return next; });
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-700 border-t-gray-700 dark:border-t-gray-300 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    const isForbidden = error === 'forbidden';
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50 dark:bg-slate-950 px-4">
        <div className="w-12 h-12 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
          <Lock className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {isForbidden ? 'Access restricted' : 'List not found'}
          </p>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1 max-w-xs">
            {isForbidden
              ? 'This list is only accessible to members of the same organization.'
              : 'This list does not exist or may have been removed.'}
          </p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-lg transition-colors mt-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to search
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col">
      <AppNavbar
        showTabs={true}
        isVisible={true}
        onTabChange={(tab) => navigate(tab === 'search' ? '/' : `/?tab=${tab}`)}
      />

      <div className="max-w-[1600px] mx-auto w-full flex-1 flex flex-col px-4 md:px-6">
        {/* Sticky sub-header */}
        <div className="sticky top-14 z-40 bg-gray-50 dark:bg-slate-950 border-b border-zinc-200 dark:border-zinc-800 py-2.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">Back to search</span>
            </button>
            <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-700 hidden sm:block" />
            <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">{totalCount} shared</span>
          </div>
        </div>

        <div className="py-6 flex flex-col gap-4">
          {/* Info card with Save all */}
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-3 md:p-5 flex flex-col md:justify-between md:flex-row gap-3 md:gap-4 overflow-hidden">
            <div className="flex items-start gap-2.5 md:gap-3 min-w-0">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-zinc-100 dark:bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                <Share className="w-4 h-4 md:w-5 md:h-5 text-zinc-600 dark:text-zinc-300" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="text-zinc-900 dark:text-zinc-100 font-semibold text-sm md:text-base">
                  {data.ownerName}'s saved candidates
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-xs md:text-sm mt-0.5">
                  {data.lastUpdated ? `Last updated ${formatDate(data.lastUpdated)}. ` : ''}
                  Save the full list to your account, or choose candidates individually.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end w-full md:w-auto">
              <button
                onClick={handleSaveAll}
                disabled={savingAll || savedAll || totalCount === 0}
                className="w-full bg-blue-600 text-white font-medium text-sm px-3.5 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 flex-shrink-0 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingAll ? (
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Heart className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                <span>{savedAll ? 'Saved!' : 'Save all'}</span>
              </button>
            </div>
          </div>

          {/* Domain pills — only when multiple domains */}
          {data.domains.length > 1 && (
            <div className="flex justify-center">
              <div className="flex flex-wrap justify-center gap-1.5 p-1">
                {[null, ...data.domains.map(d => d.domain)].map((d) => (
                  <button
                    key={d ?? '__all__'}
                    onClick={() => setActiveDomain(d)}
                    className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-all duration-200 ${
                      activeDomain === d
                        ? 'bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600 shadow-sm'
                        : 'bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                  >
                    {d === null ? 'All' : (data.domains.find(x => x.domain === d)?.label ?? d)}
                    {d !== null && (
                      <span className={`ml-1.5 text-xs ${activeDomain === d ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                        {data.domains.find(x => x.domain === d)?.engineers.length ?? 0}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Candidate grid — same layout as saved/viewed tabs */}
          {data.domains
            .filter(({ domain }) => activeDomain === null || domain === activeDomain)
            .map(({ domain, label, engineers }) => (
              <div key={domain}>
                {data.domains.length > 1 && activeDomain === null && (
                  <h2 className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-3">{label}</h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 items-stretch">
                  {(engineers as SoftwareTalent[]).map((talent, index) => (
                    <DeveloperCard
                      key={talent.id}
                      talent={talent}
                      index={index}
                      domain={domain}
                      activeTab="saved"
                      isSaved={savedAll || savedIds.has(talent.id)}
                      isViewed={false}
                      isDiscarded={false}
                      onClick={setQuickViewTalent}
                      onSave={(id) => handleSave(id, domain)}
                      onUnsave={(id) => handleUnsave(id, domain)}
                    />
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      {quickViewTalent && (
        <QuickViewModal
          talent={quickViewTalent}
          domain={(quickViewTalent.domain ?? 'SWE') as any}
          allTalent={allTalent}
          onClose={() => setQuickViewTalent(null)}
          onNavigateTalent={setQuickViewTalent}
          activeTab="saved"
          isSaved={savedAll || savedIds.has(quickViewTalent.id)}
          isDiscarded={false}
          onSave={(id) => handleSave(id, quickViewTalent.domain ?? 'SWE')}
          onUnsave={(id) => handleUnsave(id, quickViewTalent.domain ?? 'SWE')}
          hasMore={false}
          loadingMore={false}
        />
      )}
    </div>
  );
};

export default SharedListPage;
